import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hasArticles } from "@/lib/kb";
import dayjs from "dayjs";
import { fmtLogDay } from "@/lib/tz";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SignaturePad } from "@/components/SignaturePad";
import { DocumentForm } from "@/components/DocumentForm";
import { CalendarDays, FileText, LogOut, Loader2, Download, PenTool, Clock, CheckCircle2, UserRound, Users, HelpCircle } from "lucide-react";
import { sanitizeDocHtml } from "@/components/RichTextEditor";
import { useAppName } from "@/lib/branding";

const TEAL = "#2f6f6b";

function timeStr(t) { return t ? dayjs(t).format("h:mm A") : ""; }

export default function Portal() {
  const appName = useAppName();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [pid, setPid] = useState("");
  const [view, setView] = useState("roster");

  const loadMe = () => api.portalMe().then((d) => { setMe(d); setPid((cur) => cur || d.participants[0]?.name || ""); });
  useEffect(() => {
    loadMe().catch((e) => { toast.error(e.response?.data?.detail || "Could not load your portal."); });
  }, []);

  const current = me?.participants.find((p) => p.name === pid);
  const awaiting = current?.awaiting_count || 0;

  if (!me) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <span className="font-brand font-semibold text-slate-800" style={{ color: TEAL }}>{appName}</span>
          <span className="text-[11px] text-slate-400 hidden sm:inline">Participant Portal</span>
          <div className="ml-auto flex items-center gap-2">
            {me.participants.length > 1 ? (
              <Select value={pid} onValueChange={setPid}>
                <SelectTrigger className="h-9 text-sm w-56" data-testid="portal-participant-switcher"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {me.participants.map((p) => <SelectItem key={p.name} value={p.name} className="text-sm">{p.display_name}{p.is_self ? "" : ` · ${p.relationship}`}{p.awaiting_count ? ` (${p.awaiting_count} to sign)` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-sm text-slate-600 flex items-center gap-1.5" data-testid="portal-participant-name"><UserRound className="h-4 w-4 text-slate-400" />{current?.display_name}</span>
            )}
            {hasArticles(["participant"]) && (
              <Button variant="ghost" size="sm" onClick={() => nav("/portal/help")} className="text-slate-500" data-testid="portal-help"><HelpCircle className="h-4 w-4" /></Button>
            )}
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-500" data-testid="portal-logout"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5">
        <div className="text-sm text-slate-500 mb-3">Hi {me.name?.split(" ")[0] || "there"} — viewing <b className="text-slate-700">{current?.display_name}</b>{current && !current.is_self ? <span className="text-slate-400"> ({current.relationship})</span> : null}</div>

        {awaiting > 0 && (
          <button onClick={() => setView("documents")} className="w-full mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3 text-left hover:bg-amber-100/70 transition-colors" data-testid="portal-awaiting-banner">
            <PenTool className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-900">{awaiting} document{awaiting > 1 ? "s" : ""} need{awaiting > 1 ? "" : "s"} your signature</div>
              <div className="text-[11px] text-amber-700">Tap here to review and sign.</div>
            </div>
            <span className="text-amber-600 text-sm font-medium">Review →</span>
          </button>
        )}

        <div className="flex gap-1 mb-4 bg-white border border-slate-200 rounded-lg p-1 w-fit">
          <button onClick={() => setView("roster")} className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 ${view === "roster" ? "text-white" : "text-slate-600"}`} style={view === "roster" ? { backgroundColor: TEAL } : {}} data-testid="portal-tab-roster"><CalendarDays className="h-4 w-4" />Roster</button>
          <button onClick={() => setView("documents")} className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 ${view === "documents" ? "text-white" : "text-slate-600"}`} style={view === "documents" ? { backgroundColor: TEAL } : {}} data-testid="portal-tab-documents">
            <FileText className="h-4 w-4" />My Documents
            {awaiting > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid="portal-doc-badge">{awaiting}</span>}
          </button>
          <button onClick={() => setView("team")} className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 ${view === "team" ? "text-white" : "text-slate-600"}`} style={view === "team" ? { backgroundColor: TEAL } : {}} data-testid="portal-tab-team"><Users className="h-4 w-4" />My Team</button>
        </div>

        {pid && view === "roster" && <PortalRoster participant={pid} />}
        {pid && view === "documents" && <PortalDocuments participant={pid} onSigned={loadMe} />}
        {pid && view === "team" && <PortalTeam participant={pid} />}
      </div>
    </div>
  );
}

function PortalRoster({ participant }) {
  const [rows, setRows] = useState(null);
  // Default: all upcoming published shifts — no upper bound shows the full picture.
  const [range, setRange] = useState({ from: dayjs().format("YYYY-MM-DD"), to: null });
  useEffect(() => {
    setRows(null);
    api.portalRoster(participant, range.from, range.to || undefined).then(setRows).catch(() => { setRows([]); toast.error("Could not load roster."); });
  }, [participant, range]);

  const groups = useMemo(() => {
    const m = {};
    (rows || []).forEach((r) => { (m[r.shift_date] = m[r.shift_date] || []).push(r); });
    return Object.entries(m).sort();
  }, [rows]);

  const allUpcoming = !range.to;

  return (
    <div data-testid="portal-roster">
      <div className="flex flex-wrap gap-2 mb-3">
        <Button size="sm" variant={allUpcoming ? "default" : "outline"} className="h-8 text-xs" style={allUpcoming ? { backgroundColor: TEAL, color: "#fff", borderColor: TEAL } : {}} onClick={() => setRange({ from: dayjs().format("YYYY-MM-DD"), to: null })} data-testid="portal-range-upcoming">All upcoming</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setRange({ from: dayjs().format("YYYY-MM-DD"), to: dayjs().add(2, "week").format("YYYY-MM-DD") })} data-testid="portal-range-2weeks">Next 2 weeks</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setRange({ from: dayjs().startOf("month").format("YYYY-MM-DD"), to: dayjs().endOf("month").format("YYYY-MM-DD") })} data-testid="portal-range-month">This month</Button>
      </div>
      {rows === null ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
        <div className="space-y-4">
          {groups.length === 0 && <div className="text-sm text-slate-400 py-10 text-center">No published shifts in this period.</div>}
          {groups.map(([date, items]) => (
            <div key={date}>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">{dayjs(date).format("dddd, D MMMM")}</div>
              <div className="space-y-2">
                {items.map((r, i) => (
                  <div key={i}
                    className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${r.cancelled ? "border-red-200 bg-red-50/60" : "border-slate-200 bg-white"}`}
                    data-testid="portal-shift">
                    <div className={`flex items-center gap-2 text-sm font-medium ${r.cancelled ? "text-slate-400 line-through" : "text-slate-800"}`}>
                      <Clock className={`h-4 w-4 shrink-0 ${r.cancelled ? "text-red-300" : "text-slate-400"}`} />
                      {timeStr(r.start_time)} – {timeStr(r.end_time)}
                    </div>
                    <div className="flex flex-col items-end gap-0.5 shrink-0">
                      {r.cancelled ? (
                        <>
                          <Badge className="text-[10px] py-0 bg-red-100 text-red-700 border-red-300 font-bold tracking-wide" data-testid="portal-shift-cancelled-badge">CANCELLED</Badge>
                          <span className="text-[10px] text-red-500">{r.cancel_charge ? "Charged" : "No Charge"}</span>
                        </>
                      ) : r.unstaffed ? (
                        <Badge className="text-[10px] py-0 bg-amber-50 text-amber-700 border-amber-200">Staff to be confirmed</Badge>
                      ) : (
                        <span className="text-sm text-slate-600 flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5 text-slate-400" />{r.worker_first_name}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PortalTeam({ participant }) {
  const [members, setMembers] = useState(null);
  useEffect(() => {
    setMembers(null);
    api.portalTeam(participant).then(setMembers).catch(() => { setMembers([]); toast.error("Could not load your team."); });
  }, [participant]);

  return (
    <div data-testid="portal-team">
      {members === null ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
        <div>
          {members.length === 0 && <div className="text-sm text-slate-400 py-10 text-center">No support workers assigned yet.</div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {members.map((m, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col items-center gap-3" data-testid="portal-team-member">
                {m.photo ? (
                  <img src={m.photo} alt={m.first_name} className="h-16 w-16 rounded-full object-cover border-2 border-slate-100" />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center">
                    <UserRound className="h-7 w-7 text-slate-400" />
                  </div>
                )}
                <span className="text-sm font-medium text-slate-700">{m.first_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PortalDocuments({ participant, onSigned }) {
  const [docs, setDocs] = useState(null);
  const [signing, setSigning] = useState(null);   // full doc for signing dialog

  const load = () => { setDocs(null); api.portalDocuments(participant).then(setDocs).catch(() => { setDocs([]); toast.error("Could not load documents."); }); };
  useEffect(load, [participant]);

  const download = async (d) => {
    try {
      const blob = await api.portalDocumentPdf(d.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${(d.title || "document").replace(/\s+/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Download not available yet."); }
  };
  const openSign = async (d) => { try { setSigning(await api.portalDocument(d.id)); } catch { toast.error("Could not open document."); } };

  return (
    <div data-testid="portal-documents">
      {docs === null ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
        <div className="space-y-2">
          {docs.length === 0 && <div className="text-sm text-slate-400 py-10 text-center">You have no documents yet.</div>}
          {docs.map((d) => (
            <div key={d.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between gap-3" data-testid={`portal-doc-${d.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-2">{d.title}
                  {d.kind === "sa" && <Badge className="text-[9px] py-0 bg-slate-100 text-slate-500 border-slate-200">Agreement</Badge>}
                </div>
                <div className="text-[10px] text-slate-400">
                  {d.signable ? "Awaiting your signature" : d.signed_on ? `You signed ${fmtLogDay(d.signed_on)}` : d.workflow_state}
                </div>
              </div>
              {d.signable ? (
                <Button size="sm" onClick={() => openSign(d)} style={{ backgroundColor: TEAL }} data-testid={`portal-sign-${d.id}`}><PenTool className="h-3.5 w-3.5 mr-1.5" />Review & sign</Button>
              ) : d.downloadable ? (
                <Button size="sm" variant="outline" onClick={() => download(d)} data-testid={`portal-download-${d.id}`}><Download className="h-3.5 w-3.5 mr-1.5" />PDF</Button>
              ) : (
                <Badge className="text-[10px] py-0 bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" />Signed</Badge>
              )}
            </div>
          ))}
        </div>
      )}
      <SignDialog doc={signing} onClose={() => setSigning(null)} onSigned={() => { setSigning(null); load(); onSigned?.(); }} />
    </div>
  );
}

function SignDialog({ doc, onClose, onSigned }) {
  const [sig, setSig] = useState("");
  const [typed, setTyped] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSig(""); setTyped(""); setConsent(false); }, [doc]);

  const sign = async () => {
    if (!sig) return toast.error("Please draw your signature.");
    if (!typed.trim()) return toast.error("Please type your full name.");
    if (!consent) return toast.error("Please tick the confirmation box.");
    setBusy(true);
    try { await api.portalSign(doc.id, { signature: sig, signature_text: typed.trim(), consent_acknowledged: true }); toast.success("Thank you — your signature has been recorded."); onSigned(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit signature."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="portal-sign-dialog">
        {doc && <>
          <DialogHeader><DialogTitle className="text-base font-brand">{doc.title}</DialogTitle>
            <DialogDescription className="text-xs">Review the document below, then draw your signature and confirm to sign.</DialogDescription></DialogHeader>
          <div className="rounded-lg border border-slate-100 p-3 bg-slate-50/50 max-h-[45vh] overflow-auto">
            {doc.kind === "sa"
              ? <div className="prose prose-sm max-w-none text-sm text-slate-700" data-testid="portal-sa-terms" dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(doc.sa?.agreement_terms || "<p>Service agreement terms.</p>") }} />
              : <DocumentForm schema={doc.schema} values={doc.values} onChange={() => {}} disabled />}
          </div>
          <div className="space-y-3">
            <SignaturePad onChange={setSig} />
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Type your full name</Label>
              <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Full name" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-3 text-sm" data-testid="portal-sign-name" />
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="portal-sign-consent" />
              I confirm I have read and understood this document, and that this is my electronic signature.
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={sign} disabled={busy} style={{ backgroundColor: TEAL }} data-testid="portal-sign-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PenTool className="h-4 w-4 mr-1.5" />Sign</>}</Button>
          </DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
  );
}
